import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Shield, Loader2 } from 'lucide-react';

// Admin overview widget: documents expiring soon across all agents
export function ExpiringDocumentsCard({ apiUrl, adminPassword, onViewAgent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    if (!adminPassword) {
      setLoading(false);
      return;
    }
    let cancel = false;
    setLoading(true);
    fetch(`${apiUrl}/api/admin/agent-documents/expiring?within_days=${windowDays}`, {
      headers: { 'X-Admin-Password': adminPassword },
    })
      .then(r => r.ok ? r.json() : { documents: [], count: 0 })
      .then(d => { if (!cancel) setData(d); })
      .catch(() => { if (!cancel) setData({ documents: [], count: 0 }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [apiUrl, adminPassword, windowDays]);

  const docs = data?.documents || [];
  return (
    <Card data-testid="expiring-docs-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-lg flex items-center">
            <Shield className="h-5 w-5 mr-2 text-amber-600" />
            Agent Documents Expiring Soon
          </CardTitle>
          <div className="flex items-center gap-2 text-sm">
            <label className="text-gray-600">Window:</label>
            <select
              value={windowDays}
              onChange={e => setWindowDays(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
              data-testid="expiring-window-select"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            All agent documents are valid for at least the next {windowDays} days.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expiring-docs-table">
              <thead>
                <tr className="text-left border-b text-gray-600">
                  <th className="py-2 pr-3">Agent</th>
                  <th className="py-2 pr-3">Document</th>
                  <th className="py-2 pr-3">Expiry</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => {
                  const expired = d.days_until_expiry != null && d.days_until_expiry < 0;
                  const expSoon = d.days_until_expiry != null && d.days_until_expiry <= 14 && !expired;
                  return (
                    <tr key={d.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`expiring-row-${d.id}`}>
                      <td className="py-2 pr-3 font-medium text-gray-800">{d.agent_name}<br/><span className="text-xs text-gray-500">{d.agent_email}</span></td>
                      <td className="py-2 pr-3">{d.custom_name || d.document_type.replace(/_/g, ' ')}</td>
                      <td className="py-2 pr-3">{(() => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d.expiry_date || '')); const dt = m ? new Date(Number(m[1]), Number(m[2])-1, Number(m[3])) : new Date(d.expiry_date); return dt.toLocaleDateString('en-CA'); })()}</td>
                      <td className="py-2 pr-3">
                        {expired ? (
                          <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">Expired {Math.abs(d.days_until_expiry)}d ago</span>
                        ) : expSoon ? (
                          <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">{d.days_until_expiry}d left</span>
                        ) : (
                          <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">{d.days_until_expiry}d left</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <Button size="sm" variant="outline" onClick={() => onViewAgent(d.agent_id, d.agent_name)} data-testid={`expiring-view-${d.id}`}>
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ExpiringDocumentsCard;
